import { useNavigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';

const styles = {
  screen: {
    minHeight: '100vh',
    backgroundColor: '#F4F6F9',
    fontFamily: 'system-ui, sans-serif',
  },
  main: {
    marginLeft: 220,
    padding: 24,
  },
  pageTitle: {
    fontSize: 22,
    fontWeight: '700',
    color: '#0A2D5A',
    marginBottom: 4,
  },
  pageSub: {
    fontSize: 13,
    color: '#888',
    marginBottom: 24,
  },
  card: {
    backgroundColor: 'white',
    borderRadius: 12,
    border: '0.5px solid #e0e0e0',
    padding: 24,
    maxWidth: 700,
    marginBottom: 20,
  },
  consentCard: {
    backgroundColor: '#FFF9E6',
    borderRadius: 12,
    border: '1px solid #E8620A',
    borderLeft: '4px solid #E8620A',
    padding: 16,
    maxWidth: 700,
    marginBottom: 20,
  },
  consentText: {
    fontSize: 13,
    color: '#444',
    lineHeight: '22px',
  },
  consentLabel: {
    fontSize: 11,
    fontWeight: '700',
    color: '#E8620A',
    letterSpacing: 1,
    textTransform: 'uppercase',
    marginBottom: 6,
  },
  sectionTitle: {
    fontSize: 13,
    fontWeight: '700',
    color: '#0A2D5A',
    letterSpacing: 1,
    textTransform: 'uppercase',
    marginBottom: 16,
    paddingBottom: 8,
    borderBottom: '1px solid #eee',
  },
  step: {
    display: 'flex',
    gap: 16,
    marginBottom: 20,
    alignItems: 'flex-start',
  },
  stepNumber: {
    width: 32,
    height: 32,
    borderRadius: '50%',
    backgroundColor: '#E8620A',
    color: 'white',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: '700',
    fontSize: 14,
    flexShrink: 0,
  },
  stepContent: {
    flex: 1,
  },
  stepTitle: {
    fontSize: 14,
    fontWeight: '700',
    color: '#111',
    marginBottom: 4,
  },
  stepText: {
    fontSize: 13,
    color: '#555',
    lineHeight: '22px',
  },
  scriptBox: {
    backgroundColor: '#F4F6F9',
    borderLeft: '3px solid #E8620A',
    padding: 14,
    borderRadius: 8,
    marginTop: 8,
    fontSize: 13,
    color: '#333',
    lineHeight: '22px',
    fontStyle: 'italic',
  },
  tipBox: {
    backgroundColor: '#EAF3DE',
    borderRadius: 8,
    padding: 14,
    marginTop: 8,
    fontSize: 13,
    color: '#3B6D11',
    lineHeight: '22px',
  },
  notifBox: {
    backgroundColor: '#E6F1F9',
    borderRadius: 8,
    padding: 14,
    marginTop: 8,
    fontSize: 13,
    color: '#185FA5',
    lineHeight: '22px',
  },
  videoBox: {
    backgroundColor: '#F4F6F9',
    borderRadius: 8,
    padding: 24,
    textAlign: 'center',
    border: '2px dashed #ddd',
    marginTop: 8,
  },
  videoText: {
    fontSize: 13,
    color: '#888',
    marginBottom: 8,
  },
};

export default function Setup() {
  const navigate = useNavigate();

  return (
    <div style={styles.screen}>
      <Sidebar />
      <div style={styles.main}>
        <div style={styles.pageTitle}>Client Setup Guide</div>
        <div style={styles.pageSub}>Everything you need to set up a client at the point of sale.</div>

        <div style={styles.consentCard}>
          <div style={styles.consentLabel}>Agent Responsibility</div>
          <div style={styles.consentText}>
            Reminder: It is your responsibility as the agent to inform each client before setting them up with the app and their access code, and to ensure they have consented to receive Medicare plan reminders from your agency. By adding a client to Medicare Plan Guardian you are confirming that consent has been obtained.
          </div>
        </div>

        <div style={styles.card}>
          <div style={styles.sectionTitle}>Walkthrough Video</div>
          <div style={styles.videoBox}>
            <div style={styles.videoText}>Walkthrough video coming soon.</div>
            <div style={{ fontSize: 12, color: '#aaa' }}>A short video showing exactly how to set up a client and introduce the app.</div>
          </div>
        </div>

        <div style={styles.card}>
          <div style={styles.sectionTitle}>At the Point of Sale</div>

          <div style={styles.step}>
            <div style={styles.stepNumber}>1</div>
            <div style={styles.stepContent}>
              <div style={styles.stepTitle}>Add the client in your portal</div>
              <div style={styles.stepText}>
                Right after closing the sale, open your Medicare Plan Guardian portal and click Add Client. Fill in their plan details — it takes about two minutes.
              </div>
            </div>
          </div>

          <div style={styles.step}>
            <div style={styles.stepNumber}>2</div>
            <div style={styles.stepContent}>
              <div style={styles.stepTitle}>Copy their app link</div>
              <div style={styles.stepText}>
                Once saved, go to My Clients and click the green Copy App Link button next to their name — or use Send Client Links to send links to several clients at once. This copies their personal link, which takes them straight to their plan the moment they tap it. No app store, no download.
              </div>
            </div>
          </div>

          <div style={styles.step}>
            <div style={styles.stepNumber}>3</div>
            <div style={styles.stepContent}>
              <div style={styles.stepTitle}>Text it to them from your phone</div>
              <div style={styles.stepText}>
                Open a text message to your client and paste the link. They already have your number saved, so they'll open it right away.
              </div>
              <div style={styles.scriptBox}>
                "Hey [Name], I just set up your personal Medicare Plan Guardian. Tap this link and your plan will load right in your phone's browser — nothing to download, nothing to install."
              </div>
            </div>
          </div>

          <div style={styles.step}>
            <div style={styles.stepNumber}>4</div>
            <div style={styles.stepContent}>
              <div style={styles.stepTitle}>Walk them through it while you're together</div>
              <div style={styles.stepText}>
                If they haven't tapped the link yet, do it together right now — their plan loads immediately, right in the browser. Once they're in, walk them through the Home screen so they know what's there.
              </div>
              <div style={styles.scriptBox}>
                "This is your Medicare Plan Guardian. My photo and contact info are right at the top — if you ever need me, just tap Call or Schedule. You'll also get alerts from me when something affects your plan."
              </div>
            </div>
          </div>

          <div style={styles.step}>
            <div style={styles.stepNumber}>5</div>
            <div style={styles.stepContent}>
              <div style={styles.stepTitle}>Save it to their Home Screen (optional)</div>
              <div style={styles.stepText}>
                Their plan is already loaded and working — this step just adds a shortcut icon so they can get back to it with one tap next time. It's optional; everything works fine without it.
              </div>
              <div style={styles.tipBox}>
                <strong>iPhone:</strong> Tap the Share button (the square with the arrow) at the bottom of the screen, then tap Add to Home Screen.<br /><br />
                <strong>Android:</strong> Tap the ⋮ menu in the top corner of the browser, then tap Add to Home Screen (or Install, if offered).
              </div>
            </div>
          </div>

          <div style={styles.step}>
            <div style={styles.stepNumber}>6</div>
            <div style={styles.stepContent}>
              <div style={styles.stepTitle}>Allow notifications</div>
              <div style={styles.stepText}>
                When they first open the app it will ask permission to send notifications. Make sure they tap Allow — that's how they'll get their important Medicare alerts from you.
              </div>
              <div style={styles.notifBox}>
                <strong>Important:</strong> If they tap Don't Allow by mistake, go to their phone Settings, find Medicare Plan Guardian, and turn notifications on from there.
              </div>
            </div>
          </div>

        </div>

        <div style={styles.card}>
          <div style={styles.sectionTitle}>The Next Day Follow Up</div>
          <div style={styles.stepText}>
            Send a quick text the next day to make sure they found it. This takes 10 seconds and makes a big impression.
          </div>
          <div style={styles.scriptBox}>
            "Hey [Name], just checking in — were you able to get into your Medicare Plan Guardian okay? If you added the icon to your home screen, it's right there. If not, no problem — just tap the link I sent you anytime. Let me know if you need anything!"
          </div>
        </div>

      </div>
    </div>
  );
}